import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useCookieConsent, type CookieCategories } from '@/hooks/useCookieConsent';

/* ================================================================== *
 *  CookieBanner — AuthPage 히어로 슬라이드 전용 슬림 배너
 *
 *  로그인 페이지 글래스모피즘 디자인과 통일
 *  기본: 한 줄 바 (텍스트 + 쿠키 설정 + 모두 허용)
 *  확장: 카테고리별 토글 패널
 * ================================================================== */

const slideUp = keyframes`
  from { opacity: 0; transform: translateX(-50%) translateY(12px); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0); }
`;

const expandIn = keyframes`
  from { opacity: 0; max-height: 0; }
  to   { opacity: 1; max-height: 400px; }
`;

/* ---- bar ---- */

const Wrap = styled.div`
  position: fixed;
  bottom: 48px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9998;
  width: min(calc(100% - 32px), 500px);
  border-radius: 16px;
  border: 1px solid rgba(249, 115, 22, 0.15);
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(24px) saturate(1.4);
  -webkit-backdrop-filter: blur(24px) saturate(1.4);
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.06) inset,
    0 8px 32px rgba(0, 0, 0, 0.25);
  animation: ${slideUp} 0.45s cubic-bezier(0.22, 1, 0.36, 1);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 16px;
    padding: 1px;
    background: linear-gradient(
      135deg,
      rgba(249, 115, 22, 0.2),
      transparent 50%,
      rgba(249, 115, 22, 0.1)
    );
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }

  @media (max-width: 480px) {
    bottom: 40px;
    width: calc(100% - 24px);
  }
`;

const BarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  position: relative;

  @media (max-width: 480px) {
    flex-direction: column;
    text-align: center;
    gap: 10px;
    padding: 14px 16px;
  }
`;

const BarText = styled.p`
  margin: 0;
  flex: 1;
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.66);
  line-height: 1.5;
  letter-spacing: 0.01em;
`;

const BarActions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

const SettingsBtn = styled.button`
  height: 34px;
  padding: 0 14px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 200ms ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.18);
    color: rgba(255, 255, 255, 0.92);
  }
`;

const AcceptBtn = styled.button`
  height: 34px;
  padding: 0 16px;
  border-radius: 10px;
  border: 1px solid rgba(249, 115, 22, 0.25);
  background: rgba(249, 115, 22, 0.12);
  color: #F97316;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 200ms ease;

  &:hover {
    background: rgba(249, 115, 22, 0.2);
    border-color: rgba(249, 115, 22, 0.4);
  }
  &:active {
    background: rgba(249, 115, 22, 0.28);
  }
`;

/* ---- expanded detail ---- */

const DetailPanel = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  padding: 12px 18px 16px;
  animation: ${expandIn} 0.3s ease forwards;
  overflow: hidden;
`;

const CategoryRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 0;

  &:not(:last-child) {
    border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  }
`;

const CategoryText = styled.div`
  flex: 1;
  min-width: 0;
`;

const CategoryLabel = styled.div`
  font-size: 0.8125rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 2px;
`;

const CategoryDesc = styled.div`
  font-size: 0.6875rem;
  color: rgba(255, 255, 255, 0.38);
  line-height: 1.5;
`;

const BadgeAlways = styled.span`
  font-weight: 400;
  font-size: 0.6875rem;
  color: rgba(249, 115, 22, 0.6);
  margin-left: 6px;
`;

/* ---- toggle ---- */

const ToggleWrap = styled.label<{ $disabled?: boolean }>`
  flex-shrink: 0;
  position: relative;
  display: inline-flex;
  width: 38px;
  height: 22px;
  margin-top: 1px;
  cursor: ${(p) => (p.$disabled ? 'default' : 'pointer')};
  opacity: ${(p) => (p.$disabled ? 0.35 : 1)};
`;

const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  margin: -1px;
`;

const Track = styled.span<{ $on: boolean }>`
  position: absolute;
  inset: 0;
  border-radius: 11px;
  background: ${(p) => (p.$on ? 'rgba(249, 115, 22, 0.7)' : 'rgba(255, 255, 255, 0.12)')};
  transition: background 0.2s;
`;

const Thumb = styled.span<{ $on: boolean }>`
  position: absolute;
  top: 2px;
  left: ${(p) => (p.$on ? '18px' : '2px')};
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  transition: left 0.2s cubic-bezier(0.22, 1, 0.36, 1);
`;

const DetailDoneBtn = styled.button`
  display: block;
  width: 100%;
  height: 36px;
  margin-top: 12px;
  border-radius: 10px;
  border: 1px solid rgba(249, 115, 22, 0.25);
  background: rgba(249, 115, 22, 0.12);
  color: #F97316;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 200ms ease;

  &:hover {
    background: rgba(249, 115, 22, 0.2);
    border-color: rgba(249, 115, 22, 0.4);
  }
`;

/* ---- component ---- */

interface CategoryConfig {
  key: keyof CookieCategories;
  locked: boolean;
}

const CATEGORIES: CategoryConfig[] = [
  { key: 'necessary', locked: true },
  { key: 'analytics', locked: false },
  { key: 'marketing', locked: false },
];

export const CookieBanner: React.FC = () => {
  const { t } = useTranslation('common');
  const { hasConsented, acceptAll, acceptSelected } = useCookieConsent();
  const [dismissed, setDismissed] = useState(() => hasConsented());
  const [expanded, setExpanded] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  if (dismissed) return null;

  const handleAcceptAll = () => {
    acceptAll();
    setDismissed(true);
  };

  const handleSaveSelected = () => {
    acceptSelected({ analytics, marketing });
    setDismissed(true);
  };

  const togglers: Record<string, [boolean, (v: boolean) => void]> = {
    analytics: [analytics, setAnalytics],
    marketing: [marketing, setMarketing],
  };

  return (
    <Wrap role="dialog" aria-label={t('cookieBanner.title', '쿠키 설정')}>
      <BarRow>
        <BarText>
          {t('cookieBanner.barMessage', '서비스 제공 및 개선을 위해 쿠키를 사용합니다.')}
        </BarText>
        <BarActions>
          <SettingsBtn onClick={() => setExpanded((v) => !v)}>
            {t('cookieBanner.settings', '쿠키 설정')}
          </SettingsBtn>
          <AcceptBtn onClick={handleAcceptAll}>
            {t('cookieBanner.acceptAll', '모두 허용')}
          </AcceptBtn>
        </BarActions>
      </BarRow>

      {expanded && (
        <DetailPanel>
          {CATEGORIES.map(({ key, locked }) => {
            const checked = locked ? true : (togglers[key]?.[0] ?? false);
            const setChecked = locked ? undefined : togglers[key]?.[1];

            return (
              <CategoryRow key={key}>
                <ToggleWrap $disabled={locked}>
                  <HiddenCheckbox
                    checked={checked}
                    disabled={locked}
                    onChange={(e) => setChecked?.(e.target.checked)}
                    aria-label={t(`cookieBanner.${key}Label`)}
                  />
                  <Track $on={checked} />
                  <Thumb $on={checked} />
                </ToggleWrap>
                <CategoryText>
                  <CategoryLabel>
                    {t(`cookieBanner.${key}Label`, categoryLabelFallback(key))}
                  </CategoryLabel>
                  <CategoryDesc>
                    {t(`cookieBanner.${key}Desc`, categoryDescFallback(key))}
                  </CategoryDesc>
                </CategoryText>
              </CategoryRow>
            );
          })}
          <DetailDoneBtn onClick={handleSaveSelected}>
            {t('cookieBanner.confirm', '완료')}
          </DetailDoneBtn>
        </DetailPanel>
      )}
    </Wrap>
  );
};

function categoryLabelFallback(key: string): string {
  const map: Record<string, string> = {
    necessary: '꼭 필요한 쿠키(항상 활성화)',
    analytics: '분석 쿠키',
    marketing: '마케팅 성과 쿠키',
  };
  return map[key] ?? key;
}

function categoryDescFallback(key: string): string {
  const map: Record<string, string> = {
    necessary:
      '이 쿠키는 사이트가 기능하는 데 필수이기 때문에 비동의로 설정할 수 없습니다. 이 쿠키는 보안, 사용자 인증, 고객 지원 등을 지원합니다.',
    analytics:
      '이 쿠키는 방문자와 사이트의 상호작용 파악을 돕습니다. 이 쿠키를 통해 트래픽을 측정하고 사이트 성능을 개선합니다.',
    marketing:
      '이 쿠키는 마케팅 캠페인의 효율성 측정을 돕습니다.',
  };
  return map[key] ?? '';
}
